import { getIsNodeFile, getIsNodeFolder, getShareableChildIds } from 'utils';
import { DataRoomSelectableNode } from './types';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';

export const findNode = (
  nodes: DataRoomSelectableNode[],
  nodeId: string,
): DataRoomSelectableNode => {
  for (const node of nodes) {
    if (node._id === nodeId) return node;

    if (node.children?.length) {
      const found = findNode(node.children, nodeId);

      if (found) return found;
    }
  }

  return null;
};

export const checkAllCheckableNodes = (
  nodes: DataRoomSelectableNode[],
  getIsDisabled?: (node: DataRoomNode) => boolean,
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    const disabled = getIsDisabled?.(node);

    return {
      ...node,
      disabled,
      isMixed: false,
      checked: !disabled,
    };
  });

export const prepareFolderNodesForFileSelector = (
  nodes: DataRoomSelectableNode[],
  parentNode: DataRoomSelectableNode,
  getIsChecked: (
    node: DataRoomNode,
    parentChecked: boolean,
    parentMixed: boolean,
  ) => boolean,
  getIsMixed: (
    node: DataRoomNode,
    parentChecked: boolean,
    parentMixed: boolean,
  ) => boolean,
  getIsDisabled?: (node: DataRoomNode) => boolean,
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    const disabled = getIsDisabled?.(node);
    const checked = getIsChecked?.(
      node,
      parentNode?.checked,
      parentNode?.isMixed,
    );
    const isMixed = getIsNodeFolder(node)
      ? getIsMixed?.(node, parentNode?.checked, parentNode?.isMixed)
      : false;

    return {
      ...node,
      disabled,
      isMixed,
      checked: checked && !disabled,
    };
  });

export const updateFolderChildrenById = (
  nodes: DataRoomSelectableNode[],
  folderId: string,
  children: DataRoomSelectableNode[],
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    if (getIsNodeFile(node)) return node;

    if (node._id === folderId) {
      return {
        ...node,
        children,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: updateFolderChildrenById(node.children, folderId, children),
      };
    }

    return node;
  });

export const toggleAllChildren = (
  nodes: DataRoomSelectableNode[],
  checked: boolean,
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    if (node.disabled) return node;

    if (getIsNodeFile(node)) {
      return { ...node, checked };
    }

    const updatedChildren = toggleAllChildren(node.children || [], checked);

    return {
      ...node,
      checked,
      isMixed: false,
      children: updatedChildren,
    };
  });

export const toggleNode = (
  nodes: DataRoomSelectableNode[],
  nodeId: string,
  checked: boolean,
) => {
  const update = (
    currentNodes: DataRoomSelectableNode[],
  ): DataRoomSelectableNode[] =>
    currentNodes.map((node) => {
      if (node.disabled) return node;

      const isFile = getIsNodeFile(node);

      if (node._id === nodeId) {
        if (isFile || !node.children?.length) {
          return { ...node, checked, isMixed: false };
        }

        const children = toggleAllChildren(node.children || [], checked);

        return {
          ...node,
          checked,
          children,
          isMixed: false,
        };
      }

      if (node.children?.length) {
        const updatedChildren = update(node.children);

        const allDownloadableFiles = updatedChildren.filter(
          ({ disabled }) => !disabled,
        );
        const checkedFiles = allDownloadableFiles.filter(
          ({ checked }) => checked,
        );

        const newChecked = !!checkedFiles.length;
        const isMixed =
          (checkedFiles.length > 0 &&
            checkedFiles.length < allDownloadableFiles.length) ||
          !!checkedFiles.find(({ isMixed }) => isMixed);

        return {
          ...node,
          isMixed,
          checked: newChecked,
          children: updatedChildren,
        };
      }

      return node;
    });

  return update(nodes);
};

export const collectCheckedNodesForDownload = (
  nodes: DataRoomSelectableNode[],
): DataRoomSelectableNode[] =>
  nodes.reduce<DataRoomSelectableNode[]>((acc, node) => {
    if (!node.checked) return acc;

    if (!node.isMixed) return [...acc, node];

    if (node.children?.length) {
      return [...acc, ...collectCheckedNodesForDownload(node.children)];
    }

    return [...acc, node];
  }, []);

export const collectCheckedNodesForShareableLink = (
  nodes: DataRoomSelectableNode[],
  checkedFileIds: string[],
): any =>
  nodes.reduce<{ fileIds: string[]; folderIds: string[] }>(
    (acc, node) => {
      if (!node.checked) return acc;

      const isFile = getIsNodeFile(node);

      const shareableChildrenIds = getShareableChildIds(node);

      if (!node.isMixed)
        return {
          ...acc,
          folderIds: !isFile ? [...acc.folderIds, node._id] : acc.folderIds,
          fileIds: isFile
            ? [...acc.fileIds, node._id]
            : [...acc.fileIds, ...shareableChildrenIds],
        };

      // mixed and doesn't have fetched children
      if (!isFile && !node.children?.length) {
        return {
          ...acc,
          fileIds: [
            ...acc.fileIds,
            ...shareableChildrenIds.filter((_id) =>
              checkedFileIds.includes(_id),
            ),
          ],
        };
      }

      if (node.children?.length) {
        const result = collectCheckedNodesForShareableLink(
          node.children,
          checkedFileIds,
        );
        return {
          ...acc,
          folderIds: [...acc.folderIds, ...result.folderIds],
          fileIds: [...acc.fileIds, ...result.fileIds],
        };
      }

      return acc;
    },
    { fileIds: [], folderIds: [] },
  );

export const getIsNodeChecked = (
  node: DataRoomSelectableNode,
  initiallyCheckedNodes: string[],
  parentChecked?: boolean,
  parentMixed?: boolean,
  canShareBuyerFiles?: boolean,
): boolean => {
  if (!initiallyCheckedNodes?.length) return parentChecked;

  if (getIsNodeFile(node)) {
    const fileInitiallyChecked = initiallyCheckedNodes.includes(node._id);

    return !parentMixed ? parentChecked : parentChecked && fileInitiallyChecked;
  }

  const shareableChildrenIds = getShareableChildIds(node, canShareBuyerFiles);

  const folderInitiallyChecked = shareableChildrenIds.some((id) =>
    initiallyCheckedNodes.includes(id),
  );

  return !parentMixed ? parentChecked : parentChecked && folderInitiallyChecked;
};

export const getIsNodeMixed = (
  node: DataRoomSelectableNode,
  initiallyCheckedNodes: string[],
  parentChecked?: boolean,
  parentMixed?: boolean,
  canShareBuySideNodes?: boolean,
): boolean => {
  if (!parentMixed) return false;

  const shareableChildrenIds = getShareableChildIds(node, canShareBuySideNodes);

  return (
    parentChecked &&
    shareableChildrenIds.some((id) => initiallyCheckedNodes.includes(id)) &&
    !shareableChildrenIds.every((id) => initiallyCheckedNodes.includes(id))
  );
};
